import { useParams, Link, Navigate } from 'react-router-dom';
import { GALLERY, SERVICES } from '@/data/content';
import { PortfolioImage } from '@/components/site/SiteBits';
import { Seo, Breadcrumbs } from '@/components/site/Seo';
import FinalCTA from '@/components/site/FinalCTA';

export default function ProjectPage() {
  const { id } = useParams();
  const project = GALLERY.find((g) => g.id === id);

  if (!project) return <Navigate to="/gallery" replace />;

  const media = project.media || [{ type: 'image', src: project.src, alt: project.title }];
  const path = `/gallery/${project.id}`;
  const others = GALLERY.filter((g) => g.id !== project.id).slice(0, 3);
  const relatedServices = (project.relatedServices || [])
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter(Boolean);

  return (
    <div data-testid={`page-project-${project.id}`}>
      <Seo
        title={`${project.title}${project.location ? ` — ${project.location}` : ''} | HearthOak Construction`}
        description={project.caption}
        path={path}
      />

      <section className="border-b border-brand-border bg-brand-bone">
        <div className="container-x py-14 lg:py-20 max-w-4xl">
          <Breadcrumbs
            items={[{ label: 'Home', to: '/' }, { label: 'Completed Projects', to: '/gallery' }, { label: project.title }]}
            testId={`breadcrumbs-project-${project.id}`}
          />
          <div className="eyebrow mb-3">{project.category}{project.location ? ` · ${project.location}` : ''}</div>
          <h1 className="h1">{project.title}</h1>
          <p className="body-lg mt-6 max-w-2xl">{project.caption}</p>
        </div>
      </section>

      {project.story && (
        <section className="border-b border-brand-border">
          <div className="container-x py-16 lg:py-24 max-w-3xl">
            <div className="eyebrow mb-3">Project details</div>
            <h2 className="h2">The work, start to finish.</h2>
            <div className="mt-8 space-y-5 font-sans text-base lg:text-[17px] leading-[1.85] text-brand-charcoal/90" data-testid="project-story">
              {project.story.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {relatedServices.length > 0 && (
              <p className="mt-8 font-sans text-sm text-brand-muted">
                Related service:{' '}
                {relatedServices.map((s, i) => (
                  <span key={s.slug}>
                    {i > 0 && ', '}
                    <Link to={`/services/${s.slug}`} className="text-brand-maroon underline underline-offset-4" data-testid={`project-service-${s.slug}`}>
                      {s.title}
                    </Link>
                  </span>
                ))}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-16 lg:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="project-media">
            {media.map((m, i) => (
              <figure key={i} className={`border bg-brand-bone ${m.label ? 'border-2 border-brand-charcoal/60 shadow-[0_14px_36px_-18px_rgba(28,28,29,0.35)]' : 'border-brand-border'}`} data-testid={`project-media-${i}`}>
                {m.type === 'video' ? (
                  <div className="relative w-full aspect-[4/3] bg-brand-charcoal overflow-hidden">
                    <video
                      src={m.src}
                      poster={m.poster}
                      controls
                      preload="none"
                      playsInline
                      className="w-full h-full object-cover"
                      data-testid={`project-video-${i}`}
                    />
                    <span className="absolute top-3 left-3 bg-brand-charcoal/85 text-brand-bone font-sans text-[10px] uppercase tracking-[0.18em] px-2 py-1 pointer-events-none">
                      {m.label || 'Video'}
                    </span>
                  </div>
                ) : (
                  <div className="relative">
                    <PortfolioImage src={m.src} className="aspect-[4/3]" alt={m.alt || project.title} />
                    {m.label && (
                      <span className={`absolute top-3 left-3 z-10 font-sans text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 ${m.label.toLowerCase() === 'after' ? 'bg-brand-maroon text-brand-bone' : 'bg-brand-charcoal/85 text-brand-bone'}`} data-testid={`project-label-${m.label.toLowerCase()}`}>
                        {m.label}
                      </span>
                    )}
                  </div>
                )}
                {m.alt && (
                  <figcaption className="px-4 py-3 font-sans text-xs text-brand-muted">{m.alt}</figcaption>
                )}
              </figure>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-3">
            <Link to="/estimate" className="btn-primary" data-testid="project-estimate-cta">Start a project like this</Link>
            <Link to="/gallery" className="btn-secondary">Back to all projects</Link>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="border-b border-brand-border">
          <div className="container-x py-16 lg:py-24">
            <div className="eyebrow mb-3">More work</div>
            <h2 className="h2">Other completed projects</h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {others.map((g) => (
                <Link key={g.id} to={`/gallery/${g.id}`} className="group block border border-brand-border bg-brand-bone hover:border-brand-charcoal transition-colors" data-testid={`project-related-${g.id}`}>
                  <PortfolioImage src={g.cover || g.src} className="aspect-[4/3]" alt={g.title} />
                  <div className="p-5">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-brand-maroon">{g.category}</div>
                    <div className="mt-1 font-serif text-lg text-brand-charcoal group-hover:text-brand-maroon transition-colors">{g.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FinalCTA />
    </div>
  );
}
